import React, {
  useState, useEffect, useImperativeHandle, forwardRef
} from 'react';
import { PropTypes } from 'prop-types';
import {
  Container, Typography, Grid, Card, CardContent
} from '@mui/material';
import {
  useFormContext, useFieldArray
} from 'react-hook-form';
import FormTextField from '@seccon/core/components/FormTextField';
import FormDropDown from '@seccon/core/components/FormDropDown';
import FormAutocomplete from '@seccon/core/components/FormAutocomplete';
import { fetchStateOptions } from '@seccon/shared/utils/destinationUtils';

import formatPhoneField from '../formatPhoneField';

const EmergencyContactsForm = forwardRef(({ initialData }, ref) => {
  const {
    control, trigger, formState: { errors }, watch, setValue
  } = useFormContext({});

  const { fields, append } = useFieldArray({
    control,
    name: 'foreignTravel.emergencyContacts'
  });
  const names = watch().foreignTravel.emergencyContacts;

  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    if (fields.length === 0) {
      append({
        name: '', streetAddress: '', streetAddress2: '', city: '', state: '', zip: '', relationship: '', phoneHome: '', phoneWork: '', phoneMobile: ''
      });
      append({
        name: '', streetAddress: '', streetAddress2: '', city: '', state: '', zip: '', relationship: '', phoneHome: '', phoneWork: '', phoneMobile: ''
      });
    } else if (fields.length === 1) {
      append({
        name: '', streetAddress: '', streetAddress2: '', city: '', state: '', zip: '', relationship: '', phoneHome: '', phoneWork: '', phoneMobile: ''
      });
    }
  }, [append, fields.length]);

  const onValidate = async () => {
    const isValid = trigger('foreignTravel.emergencyContacts');
    // const isValid = await trigger(); // Trigger validation for all fields
    if (isValid) {
      console.log('Validation successful');
      // Handle successful validation logic here
    } else {
      console.log('Validation failed');
      // Handle validation failure logic here
    }
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isValid = await onValidate();
      return isValid;
    }
  }));

  return (
    <Container>
      <form>
        {fields.map((item, index) => (
          <Card key={item.id} variant='outlined' style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant='h5'>
                    Emergency Contact #{index + 1}: {names[index].name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Name'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].name`}
                    required={index === 0}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Street Address'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].streetAddress`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Street Address2'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].streetAddress2`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='City'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].city`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormAutocomplete
                    control={control}
                    formErrors={errors}
                    label='State'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].state`}
                    freeSolo={false}
                    options={stateOptions}
                    fetchedOptions={(searchText) => fetchStateOptions(searchText, setStateOptions)}
                    noOptionsText='Type to search for States'
                    disableClearable={false}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='ZIP Code'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].zip`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormDropDown
                    control={control}
                    formErrors={errors}
                    label='Relationship'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].relationship`}
                    required={index === 0}
                    options={initialData.data}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Home Phone'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].phoneHome`}
                    onBlur={(e) => formatPhoneField(`foreignTravel.emergencyContacts[${ index }].phoneHome`, setValue, e.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Work Phone'
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].phoneWork`}
                    onBlur={(e) => formatPhoneField(`foreignTravel.emergencyContacts[${ index }].phoneWork`, setValue, e.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    control={control}
                    formErrors={errors}
                    label='Cell Phone'
                    required={index === 0}
                    formFieldId={`foreignTravel.emergencyContacts[${ index }].phoneMobile`}
                    onBlur={(e) => formatPhoneField(`foreignTravel.emergencyContacts[${ index }].phoneMobile`, setValue, e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </form>
    </Container>
  );
});

EmergencyContactsForm.propTypes = {
  initialData: PropTypes.object.isRequired
};

export default EmergencyContactsForm;
